/**
 * LAWD Sync Service
 * 
 * Local-first architecture with server synchronization.
 * Driver's device is the source of truth - server is synchronized backup.
 */

import { useState, useEffect } from 'react';
import { api, API_BASE } from '../utils/api';

const SYNC_INTERVAL_MS = 45 * 60 * 1000; // 45 minutes
const RETRY_INTERVALS = [60000, 120000, 300000, 600000]; // 1m, 2m, 5m, 10m
const STORAGE_KEY = 'lawd_sync_queue';
const LAST_SYNC_KEY = 'lawd_last_sync';
const LOCAL_STATE_KEY = 'lawd_local_state';

class SyncService {
  constructor() {
    this.queue = this.loadQueue();
    this.retryIndex = 0;
    this.syncTimer = null;
    this.retryTimer = null;
    this.listeners = new Set();
    this.isOnline = navigator.onLine;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    // Start sync timer
    this.startSyncTimer();
  }

  // Subscribe to sync status changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners() {
    const status = this.getStatus();
    this.listeners.forEach(cb => cb(status));
  }

  // Get current sync status
  getStatus() {
    const lastSync = this.getLastSyncTime();
    const queueLength = this.queue.length;
    const timeSinceSync = lastSync ? Date.now() - lastSync : null;
    
    let status = 'synced';
    if (queueLength > 0) {
      status = 'pending';
    }
    if (timeSinceSync && timeSinceSync > 2 * 60 * 60 * 1000) {
      status = 'critical'; // > 2 hours
    } else if (timeSinceSync && timeSinceSync > 45 * 60 * 1000) {
      status = 'delayed'; // > 45 min
    }
    
    return {
      status,
      lastSync,
      timeSinceSync,
      queueLength,
      isOnline: this.isOnline
    };
  }

  // Load queue from localStorage
  loadQueue() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // Save queue to localStorage
  saveQueue() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    this.notifyListeners();
  }

  // Get last sync timestamp
  getLastSyncTime() {
    const stored = localStorage.getItem(LAST_SYNC_KEY);
    return stored ? parseInt(stored, 10) : null;
  }

  // Update last sync timestamp
  updateLastSyncTime() {
    localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
    this.notifyListeners();
  }

  // Get local driver state
  getLocalState() {
    try {
      const stored = localStorage.getItem(LOCAL_STATE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  // Save local driver state
  saveLocalState(state) {
    localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify({
      ...state,
      localUpdatedAt: Date.now()
    }));
  }

  // Queue an action for sync
  queueAction(action) {
    const queuedAction = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...action,
      deviceTimestamp: new Date().toISOString(),
      queuedAt: Date.now()
    };
    
    this.queue.push(queuedAction);
    this.saveQueue();
    
    // Try to sync immediately if online
    if (this.isOnline) {
      this.attemptSync();
    }
    
    return queuedAction;
  }

  // Start segment locally and queue for sync
  startSegment(driverId, segmentType, vehicleId = null) {
    const now = new Date();
    const localSegment = {
      segmentId: `local-${Date.now()}`,
      driverId,
      segmentType,
      vehicleId,
      startTs: now.toISOString(),
      endTs: null,
      isLocal: true
    };
    
    // Update local state
    const currentState = this.getLocalState() || {};
    this.saveLocalState({
      ...currentState,
      currentSegment: localSegment,
      segmentType,
      segmentStartTime: now.getTime()
    });
    
    // Queue for server sync
    this.queueAction({
      type: 'START_SEGMENT',
      driverId,
      segmentType,
      vehicleId,
      source: 'DRIVER_APP'
    });
    
    return localSegment;
  }

  // Stop segment locally and queue for sync
  stopSegment(driverId, segmentId, notes = null) {
    const now = new Date();
    
    // Update local state
    const currentState = this.getLocalState() || {};
    const previousSegment = currentState.currentSegment;
    
    this.saveLocalState({
      ...currentState,
      currentSegment: null,
      previousSegmentEnd: now.getTime(),
      segmentType: previousSegment?.segmentType === 'WORK' ? 'REST' : 'WORK',
      segmentStartTime: now.getTime()
    });
    
    // Queue for server sync
    this.queueAction({
      type: 'STOP_SEGMENT',
      driverId,
      segmentId,
      notes
    });
    
    return { endTs: now.toISOString() };
  }

  // Attempt to sync queued actions
  async attemptSync() {
    if (!this.isOnline || this.queue.length === 0) {
      return { success: true, synced: 0 };
    }
    
    const hasSession = localStorage.getItem('lawd_expires_at');
    if (!hasSession) {
      return { success: false, error: 'No auth session' };
    }
    
    // Use API_BASE imported from utils/api.js
    let synced = 0;
    const failedActions = [];
    
    for (const action of [...this.queue]) {
      try {
        let endpoint, method, body;
        
        switch (action.type) {
          case 'START_SEGMENT':
            endpoint = '/api/segments/start';
            method = 'POST';
            body = {
              driver_id: action.driverId,
              segment_type: action.segmentType,
              vehicle_id: action.vehicleId,
              source: action.source,
              device_timestamp: action.deviceTimestamp
            };
            break;
            
          case 'STOP_SEGMENT':
            endpoint = '/api/segments/stop';
            method = 'POST';
            body = {
              segment_id: action.segmentId,
              notes: action.notes,
              device_timestamp: action.deviceTimestamp
            };
            break;
            
          case 'SWITCH_DIARY':
            endpoint = `/api/drivers/${action.driverId}/switch-diary?target_type=${action.targetType}`;
            method = 'POST';
            body = {};
            break;
            
          default:
            console.warn('Unknown action type:', action.type);
            continue;
        }
        
        try {
          await api(endpoint, {
            method,
            body: JSON.stringify(body)
          });
          // Remove from queue on success
          this.queue = this.queue.filter(a => a.id !== action.id);
          synced++;
        } catch (err) {
          failedActions.push(action);
        }
      } catch (err) {
        console.error('Sync failed for action:', action, err);
        failedActions.push(action);
      }
    }
    
    this.queue = failedActions;
    this.saveQueue();
    
    if (synced > 0) {
      this.updateLastSyncTime();
      this.retryIndex = 0; // Reset retry backoff on success
    }
    
    return { success: failedActions.length === 0, synced, failed: failedActions.length };
  }

  // Full state sync with server
  async syncState(driverId) {
    if (!this.isOnline) {
      return { success: false, error: 'Offline' };
    }
    
    const hasSession = localStorage.getItem('lawd_expires_at');
    if (!hasSession) {
      return { success: false, error: 'No auth session' };
    }
    
    try {
      // First, flush any queued actions
      await this.attemptSync();
      
      // Then fetch fresh state from server
      const serverState = await api(`/drivers/${driverId}/state`);
      this.saveLocalState(serverState);
      this.updateLastSyncTime();
      return { success: true, state: serverState };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  // Handle coming online
  handleOnline() {
    this.isOnline = true;
    this.notifyListeners();
    
    // Immediately try to sync
    this.attemptSync();
  }

  // Handle going offline
  handleOffline() {
    this.isOnline = false;
    this.notifyListeners();
  }

  // Start the periodic sync timer
  startSyncTimer() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
    
    this.syncTimer = setInterval(() => {
      if (this.isOnline) {
        this.attemptSync();
      }
    }, SYNC_INTERVAL_MS);
  }

  // Schedule retry with backoff
  scheduleRetry() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    
    const delay = RETRY_INTERVALS[Math.min(this.retryIndex, RETRY_INTERVALS.length - 1)];
    this.retryIndex++;
    
    this.retryTimer = setTimeout(() => {
      if (this.isOnline && this.queue.length > 0) {
        this.attemptSync();
      }
    }, delay);
  }

  // Clean up
  destroy() {
    if (this.syncTimer) clearInterval(this.syncTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
}

// Singleton instance
export const syncService = new SyncService();

// React hook for sync status
export function useSyncStatus() {
  const [status, setStatus] = useState(syncService.getStatus());
  
  useEffect(() => {
    return syncService.subscribe(setStatus);
  }, []);
  
  return status;
}
